import { defineComponent } from "vue";
import moment from "moment";
import { ValidationController } from "aurelia-validation";
import { Container } from "aurelia-dependency-injection";

import type { ITaskDetailsPayload } from "../../../../../resources-vue/vue-interfaces/i-task-details";
import { IReassignVisitDetails } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { IDeleteReturnReason, IGetReturnReasons } from "../../../../../resources-vue/vue-interfaces/i-task";
import { ConfirmationStatus, ConfirmationStatusEnum, VisitStatusEnum } from "../../../../../enums/enums";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { TaskService } from "../../../../../services/task.service";
import { PhysiciansService } from "../../../../../services/physician-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { VisitDetails } from "../../../../../models/visit-details";
import { formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { IHHScheduleEvent } from "../../../../../resources-vue/vue-interfaces/i-hh-schedule-event";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TimePicker from "../../../../../resources-vue/vue-custom-elements/TimePicker/TimePicker.vue";
import DatePicker from "../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import DayPicker from "../../../../../resources-vue/vue-custom-elements/DayPicker/DayPicker.vue";
import MultiDatePicker from "../../../../../resources-vue/vue-custom-elements/MultiDatePicker/MultiDatePicker.vue";
import GroupDropdownSelect from "../../../../../resources-vue/vue-custom-elements/GroupDropdownSelect/GroupDropdownSelect.vue";
import CommentInput from "../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import VisitAttachmentsHH from "../VisitAttachmentsHH/VisitAttachmentsHH.vue";

export default defineComponent({
    inject: ["dialogRef"],
    components: {
        FormSection,
        TimePicker,
        DatePicker,
        DayPicker,
        MultiDatePicker,
        GroupDropdownSelect,
        CommentInput,
        TypeaheadInput,
        VisitAttachmentsHH
    },

    props: {
        visitDetails: { type: Object, default: {} as IReassignVisitDetails },
        cancel: { type: Function, default: null },
        ok: { type: Function, default: null },
        isSaving: { type: Boolean, default: false },
        reassign: { type: Function },
        isPlanner: { type: Boolean, default: false }
    },

    data(vm) {
        return {
            _taskService: null,
            _toastrService: null,
            _physicianService: null,
            _dialogRef: this.dialogRef as any,
            controller: ValidationController as object,
            taskDetails: {} as VisitDetails,
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            returnReasons: [],
            isSavingReason: false as boolean,
            showPhyicianField: false as boolean,
            showAssignedToList: false as boolean,
            assignedToName: "",
            isEpisodeSelectionChecked: false as boolean,
            isLoading: false as boolean,
            isSavingVisit: false as boolean,
            disciplineTaskType: "",
            physician: {},
            employeeList: [],
            scheduleEmployee: {} as ITypeaheadOptions,
            confirmationStatusEnum: ConfirmationStatus,
            confirmationStatus: 0,
            commentInputClass: { input: "w-35" },
            scheduleEvent: {} as IHHScheduleEvent,
            formatDate,
            enumToMap,
            nVATaskId: ""
        };
    },

    created() {
        this._physicianService = Container.instance.get(PhysiciansService);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this.controller = Container.instance.get(ValidationController);
    },

    mounted() {
        this.getTaskDetails();
    },

    watch: {
        confirmationStatus(newValue) {
            if (newValue == ConfirmationStatusEnum.VisitConfirmed) {
                this.taskDetails.scheduleEvent.confirmationStatus = ConfirmationStatusEnum.VisitConfirmed;
            } else if (newValue == ConfirmationStatusEnum.ResceduleVisit) {
                this.taskDetails.scheduleEvent.confirmationStatus = ConfirmationStatusEnum.ResceduleVisit;
            }
        },
        scheduleEmployee(newValue: ITypeaheadOptions) {
            if (!!newValue) {
                const employeeDetails = this.employeeList.find((emp) => {
                    return emp.id == newValue.value;
                });
                if (employeeDetails) {
                    this.scheduleEvent.userId = employeeDetails.id;
                    this.scheduleEvent.userFirstName = employeeDetails.firstName;
                    this.scheduleEvent.userLastName = employeeDetails.lastName;
                    this.scheduleEvent.userCredentials = employeeDetails.credentials;
                }
            }
        }
    },

    computed: {
        areFieldsEnabled() {
            if (this.visitDetails.IsNonVisitAssignment) {
                return true;
            } else if (this.isPlanner) {
                return (
                    this.visitDetails.StatusName === VisitStatusEnum.NotYetDue ||
                    this.visitDetails.StatusName === VisitStatusEnum.NotStarted
                );
            } else {
                return (
                    this.visitDetails.Status === VisitStatusEnum.NotYetDue ||
                    this.visitDetails.Status === VisitStatusEnum.NotStarted
                );
            }
        }
    },

    methods: {
        isReturnedWithSignature() {
            return this.taskDetails?.scheduleEvent?.scheduleStatus == 135;
        },

        async getTaskDetails() {
            Object.assign(this.visitDetails, {});
            try {
                this.isLoading = true;
                const payload: ITaskDetailsPayload = {
                    application: this.visitDetails.Application,
                    agencyId: this.visitDetails.AgencyId,
                    eventId: this.visitDetails.Id,
                    episodeId: this.visitDetails.EpisodeId,
                    patientId: this.visitDetails.PatientId,
                    IsNonVisit: this.visitDetails.IsNonVisitAssignment
                };

                const visitDetails = await this._taskService.getHHVisitDetails(payload);
                this.taskDetails = visitDetails;
                this.scheduleEvent = visitDetails?.scheduleEvent
                    ? visitDetails.scheduleEvent
                    : ({} as IHHScheduleEvent);
                this.disciplineTaskType = `${String(visitDetails.scheduleEvent.disciplineTask)}|${String(
                    visitDetails.scheduleEvent.customId
                )}`;
                this.nVATaskId = this.taskDetails.scheduleEvent.nvaTaskId;
                this.assignedToName =
                    this.taskDetails.scheduleEvent.userFirstName +
                    " " +
                    this.taskDetails.scheduleEvent.userLastName +
                    " " +
                    this.taskDetails.scheduleEvent.userCredentials.join(", ");
                if (
                    this.visitDetails.Status === "Submitted (Pending QA Review)" ||
                    this.visitDetails.Status === "Returned w/ Physician Signature"
                ) {
                    this.showPhyicianField = true;
                    this.physician = {
                        name: this.taskDetails.scheduleEvent.physicianName,
                        value: this.taskDetails.scheduleEvent.physicianId
                    };
                }
                if (
                    this.visitDetails.Status === "Reopened" ||
                    this.visitDetails.Status === "Not Yet Started" ||
                    this.visitDetails.Status === "Saved" ||
                    this.visitDetails.Status === "Returned For Review" ||
                    this.visitDetails.Status === "Returned for Clinician Signature"
                ) {
                    const employees = await this._taskService.assignToList({
                        CustomTaskId: this.visitDetails.Id,
                        AgencyId: this.visitDetails.AgencyId,
                        Application: this.visitDetails.Application
                    });
                    this.employeeList = employees;
                    this.showAssignedToList = true;
                }
                if (
                    this.isPlanner &&
                    (this.visitDetails.StatusName === "Reopened" ||
                        this.visitDetails.StatusName === "Not Yet Started" ||
                        this.visitDetails.StatusName === "Saved" ||
                        this.visitDetails.StatusName === "Returned For Review" ||
                        this.visitDetails.StatusName === "Returned for Clinician Signature")
                ) {
                    const employees = await this._taskService.assignToList({
                        CustomTaskId: this.visitDetails.Id,
                        AgencyId: this.visitDetails.AgencyId,
                        Application: this.visitDetails.Application
                    });
                    this.employeeList = employees;
                    this.showAssignedToList = true;
                }
                if (this.taskDetails.scheduleEvent.userId) {
                    const employeeDetails = this.employeeList.find((emp) => {
                        return emp.id == this.taskDetails.scheduleEvent.userId;
                    });
                    this.scheduleEmployee = {
                        value: employeeDetails.id,
                        name: `${employeeDetails.firstName} ${employeeDetails.lastName}`
                    };
                }
                this.getReturnReasonList();
                if (this.taskDetails.scheduleEvent.confirmationStatus == ConfirmationStatusEnum.VisitConfirmed)
                    this.confirmationStatus = ConfirmationStatusEnum.VisitConfirmed;
                if (this.taskDetails.scheduleEvent.confirmationStatus == ConfirmationStatusEnum.ResceduleVisit)
                    this.confirmationStatus = ConfirmationStatusEnum.ResceduleVisit;
            } catch (error) {
                console.log(error);
            } finally {
                this.isLoading = false;
            }
        },

        async getPhysicianList(filter: string, limit: number) {
            const data = await this._physicianService.getPhysiciansByName({
                name: filter,
                page: 1,
                pageLength: limit,
                providerIds: [],
                application: this.visitDetails.Application,
                accountId: this.taskDetails.scheduleEvent.agencyId
            });
            return data.items?.map((item: any) => ({ value: item.id, name: item.name }));
        },

        physicianChanged(newValue: any) {
            if (newValue != null) {
                this.scheduleEvent.physicianId = newValue.value;
            }
        },

        changeVisitTimeIn(time: any) {
            this.scheduleEvent.timeIn = time;
        },

        changeVisitTimeOut(time: any) {
            this.scheduleEvent.timeOut = time;
        },

        disciplineTaskTypeChanged(newValue: string) {
            if (newValue) {
                this.scheduleEvent.disciplineTask = Number(newValue.split("|")[0]);
                this.scheduleEvent.customId = Number(newValue.split("|")[1]);
            }
        },

        serviceLocationChange(newValue: string) {
            this.scheduleEvent.serviceLocation = newValue;
        },

        onEpisodeListChange(Id: string) {
            this.scheduleEvent.episodeId = Id;
        },

        changeTravelStartTime(time: any) {
            this.scheduleEvent.travelTimeIn = time;
        },

        changeTravelEndTime(time: any) {
            this.scheduleEvent.travelTimeOut = time;
        },

        closeDialog() {
            const promptOptions: IPromptOptions = {
                message: "Are you sure you want to leave the page? Please make sure you saved your work.",
                okText: "Yes, I'm Sure",
                cancelText: "Never Mind"
            };

            this._dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data === "success") {
                        this.cancel();
                    }
                }
            });
        },

        formatTime(dateTime: Date) {
            return dateTime
                ? new Date(dateTime).toLocaleTimeString("en-us", {
                      hour: "2-digit",
                      minute: "2-digit"
                  })
                : "";
        },

        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.employeeList.filter((employee) => {
                    const fullName = `${employee.firstName} ${employee.lastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item) => {
                    return {
                        name: `${item.firstName} ${item.lastName}`,
                        value: item.id
                    };
                });
            } else {
                return this.employeeList?.map((item) => ({
                    value: item.id,
                    name: `${item.firstName} ${item.lastName}`
                }));
            }
        },

        changescheduleEmployee(data: any) {
            this.scheduleEmployee = data;
        },

        confirmDeleteReason() {
            this.isDeleteRequested = true;
        },

        taskDateChanged(date: any, name: string) {
            if (name === "startDateTime") {
                this.visitDetails.StartDate = date;
            } else if (name === "endDateTime") {
                this.visitDetails.EndDate = date;
            }
        },

        handleChangeStatus(status: string) {
            this.scheduleEvent.status = status;
            this.scheduleEvent.scheduleStatus = Number(status);
        },

        cancelDeleteReason() {
            this.isDeleteRequested = false;
        },

        async deleteReason(id: number) {
            try {
                this.isDeleteInProgress = true;
                let deleteQuery: IDeleteReturnReason = {
                    application: this.visitDetails.Application,
                    id: id
                };
                await this._taskService.deleteReturnReason(deleteQuery);
                this.getReturnReasonList();
                this._toastrService.success({
                    message: `Return Reason has been successfully deleted.`,
                    title: `Return Reason Deleted`
                });
            } catch (error) {
                console.log(error);
                this._toastrService.error({
                    title: `Error - Delete Return Reason`,
                    message: `There was a problem while deleting the Return Reason. Please try again.`
                });
                this.isDeleteInProgress = false;
            }
        },

        async getReturnReasonList() {
            const payload: IGetReturnReasons = {
                application: this.visitDetails.Application,
                agencyId: this.visitDetails.AgencyId,
                eventId: this.visitDetails.Id,
                episodeId: this.visitDetails.EpisodeId,
                patientId: this.visitDetails.PatientId,
                userId: this.visitDetails.UserId
            };
            const returnReasons = await this._taskService.getReturnReasonList(payload);
            this.returnReasons = returnReasons.data;
        },

        resultUpdatedAppended(value: string) {
            this.scheduleEvent.comments += value;
        },
        resultUpdated(value: string) {
            this.scheduleEvent.comments = value;
        },

        async updateVisitDetails() {
            if (!!this.taskDetails) {
                try {
                    this.isSavingVisit = true;
                    this.scheduleEvent.application = this.visitDetails.Application;
                    this.scheduleEvent.eventDate = !this.visitDetails.IsNonVisitAssignment
                        ? moment(this.scheduleEvent.eventDate).format("MM/DD/YYYY")
                        : "01/01/0001";
                    this.scheduleEvent.visitDate = moment(this.scheduleEvent.visitDate).format("MM/DD/YYYY");
                    this.scheduleEvent.status = this.scheduleEvent.status;
                    this.scheduleEvent.isNonVisit = this.visitDetails.IsNonVisitAssignment;
                    this.scheduleEvent.nvaTaskId = this.nVATaskId;
                    await this._taskService.updateHHVisitDetails(this.scheduleEvent);
                    this._toastrService.success({
                        title: this.visitDetails.IsNonVisitAssignment ? "Non-Visit Task Updated" : "Visit Updated",
                        message: this.visitDetails.IsNonVisitAssignment
                            ? "`The Task has been successfully Updated"
                            : "Visit has been successfully Updated"
                    });
                    // trigger callback
                    this.ok("success");
                } catch (e) {
                    this._toastrService.error({
                        title: this.visitDetails.IsNonVisitAssignment
                            ? `Non-Visit Task Update Failed`
                            : "Visit Update Failed",
                        message: this.visitDetails.IsNonVisitAssignment
                            ? `Updating Task Operation Failed, Please Contact Axxess if issue still persists.`
                            : `Updating Visit Operation Failed, Please Contact Axxess if issue still persists.`
                    });
                } finally {
                    this.isSavingVisit = false;
                }
            }
        },
        handleDateChange(date: string, name: string) {
            if (name === "scheduleDateTime") {
                this.scheduleEvent.eventDate = date;
            }
            if (name === "actualDateTime") {
                this.scheduleEvent.visitDate = date;
            }
            if (name === "nvaDateTime") this.scheduleEvent.nvaStartDate = date;
            if (name === "nvaPaidDate") this.scheduleEvent.nvaPaidDate = date;
        },
        handleTypedDateChange(e: any) {
            const name = e.target.name;
            const value = e.target.value;
            if (name === "scheduleDateTime") {
                this.scheduleEvent.eventDate = value;
            }
            if (name === "actualDateTime") {
                this.scheduleEvent.visitDate = value;
            }
            if (name === "nvaDateTime") this.scheduleEvent.nvaStartDate = value;
            if (name === "nvaPaidDate") this.scheduleEvent.nvaPaidDate = value;
        }
    }
});
